import { fetchJson, url } from "./lib/http";
import {
  CLOB_AUTH_DOMAIN,
  CLOB_AUTH_TYPES,
  type ClobOrderRequest,
  type OrderSigner,
  type SignedClobOrder,
} from "./signing";
import type { PolymarketClobBook, PolymarketGammaMarket } from "./wire";

export const POLYMARKET_GAMMA = "https://gamma-api.polymarket.com";
export const POLYMARKET_CLOB = "https://clob.polymarket.com";

export type PolymarketClientConfig = {
  gammaUrl?: string;
  clobUrl?: string;
};

export type ClobCredentials = {
  apiKey: string;
  secret: string;
  passphrase: string;
};

export type ClobOrderResponse = {
  success: boolean;
  orderID?: string;
  errorMsg?: string;
  status?: string;
  transactionsHashes?: string[];
};

export class PolymarketClient {
  readonly gammaUrl: string;
  readonly clobUrl: string;
  private auth: { address: string; credentials: ClobCredentials } | null = null;

  constructor(config: PolymarketClientConfig = {}) {
    this.gammaUrl = config.gammaUrl ?? POLYMARKET_GAMMA;
    this.clobUrl = config.clobUrl ?? POLYMARKET_CLOB;
  }

  setAuth(address: string, credentials: ClobCredentials) {
    this.auth = { address, credentials };
  }

  get isAuthenticated(): boolean {
    return this.auth !== null;
  }

  // -------------------------------------------------------------------------
  // Read endpoints (public — Cloudflare-cached)
  // -------------------------------------------------------------------------

  async listMarkets(opts: {
    limit?: number;
    closed?: boolean;
    tagSlug?: string;
    order?: string;
    ascending?: boolean;
  } = {}): Promise<PolymarketGammaMarket[]> {
    const u = url(this.gammaUrl, "markets", {
      closed: opts.closed ?? false,
      limit: Math.min(opts.limit ?? 50, 200),
      tag_slug: opts.tagSlug,
      order: opts.order ?? "volume24hr",
      ascending: opts.ascending ?? false,
    });
    return fetchJson<PolymarketGammaMarket[]>(u, { edgeCache: { ttlSeconds: 60 } });
  }

  async getMarketByConditionId(conditionId: string): Promise<PolymarketGammaMarket | null> {
    try {
      const rows = await fetchJson<PolymarketGammaMarket[]>(
        url(this.gammaUrl, "markets", { condition_ids: conditionId }),
        { edgeCache: { ttlSeconds: 30 } }
      );
      return rows.find((r) => r.conditionId === conditionId) ?? rows[0] ?? null;
    } catch {
      return null;
    }
  }

  async getMarketBySlug(slug: string): Promise<PolymarketGammaMarket | null> {
    try {
      const rows = await fetchJson<PolymarketGammaMarket[]>(
        url(this.gammaUrl, "markets", { slug }),
        { edgeCache: { ttlSeconds: 30 } }
      );
      return rows[0] ?? null;
    } catch {
      return null;
    }
  }

  async fetchBook(tokenId: string): Promise<PolymarketClobBook | null> {
    try {
      return await fetchJson<PolymarketClobBook>(
        url(this.clobUrl, "book", { token_id: tokenId }),
        { edgeCache: { ttlSeconds: 5 } }
      );
    } catch {
      return null;
    }
  }

  // -------------------------------------------------------------------------
  // L1 auth — derive API key trio (apiKey/secret/passphrase) from a signer.
  // -------------------------------------------------------------------------

  /**
   * Try `POST /auth/api-key` (create) first. If the server has already issued
   * a key for this (address, nonce), fall back to `GET /auth/derive-api-key`
   * which returns the existing trio.
   *
   * Both require the L1 ClobAuth EIP-712 signature in the headers — we sign
   * with the supplied signer (any viem WalletClient or PrivateKeyAccount).
   */
  async createOrDeriveApiKey(
    signer: OrderSigner,
    nonce = 0,
  ): Promise<ClobCredentials> {
    const timestamp = String(Math.floor(Date.now() / 1000));
    const signature = await signer.signTypedData({
      domain: CLOB_AUTH_DOMAIN as unknown as Record<string, unknown>,
      types: CLOB_AUTH_TYPES as unknown as Record<
        string,
        ReadonlyArray<{ name: string; type: string }>
      >,
      primaryType: "ClobAuth",
      message: {
        address: signer.address,
        timestamp,
        nonce,
        message: "This message attests that I control the given wallet",
      },
    });

    const headers: Record<string, string> = {
      POLY_ADDRESS: signer.address,
      POLY_SIGNATURE: signature,
      POLY_TIMESTAMP: timestamp,
      POLY_NONCE: String(nonce),
    };

    const createRes = await fetch(`${this.clobUrl}/auth/api-key`, {
      method: "POST",
      headers,
      signal: AbortSignal.timeout(15_000),
    });
    if (createRes.ok) {
      return (await createRes.json()) as ClobCredentials;
    }

    const deriveRes = await fetch(`${this.clobUrl}/auth/derive-api-key`, {
      method: "GET",
      headers,
      signal: AbortSignal.timeout(15_000),
    });
    if (!deriveRes.ok) {
      const body = await deriveRes.text().catch(() => "");
      throw new Error(`CLOB derive-api-key ${deriveRes.status}: ${body}`);
    }
    return (await deriveRes.json()) as ClobCredentials;
  }

  // -------------------------------------------------------------------------
  // L2 auth — POST signed CTF order with HMAC headers.
  // -------------------------------------------------------------------------

  async placeOrder(
    signedOrder: SignedClobOrder,
    request: ClobOrderRequest,
  ): Promise<ClobOrderResponse> {
    if (!this.auth) throw new Error("polymarket: setAuth() before placeOrder");
    // CLOB v2 wire format: `owner` is the API-key UUID (not the wallet address);
    // `side` rides as "BUY"/"SELL" even though the EIP-712 signature is over uint8.
    const payload = {
      order: serializeSignedOrder(signedOrder, request.side),
      owner: this.auth.credentials.apiKey,
      orderType: request.timeInForce,
    };
    return this.clobPostAuth<ClobOrderResponse>("/order", payload);
  }

  async cancelOrder(orderId: string): Promise<{ success: boolean }> {
    if (!this.auth) throw new Error("polymarket: setAuth() before cancelOrder");
    return this.clobDeleteAuth<{ success: boolean }>(`/order/${orderId}`);
  }

  // -------------------------------------------------------------------------
  // HMAC-SHA256 L2 auth (Web Crypto — Workers-compatible)
  // -------------------------------------------------------------------------

  private async buildHmacSignature(
    method: string,
    requestPath: string,
    body: string,
    timestamp: string,
  ): Promise<string> {
    if (!this.auth) throw new Error("polymarket: no auth");
    const secretNormalized = this.auth.credentials.secret
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const secretBytes = base64Decode(secretNormalized);
    const key = await crypto.subtle.importKey(
      "raw",
      secretBytes as unknown as ArrayBuffer,
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"],
    );
    const message = `${timestamp}${method}${requestPath}${body}`;
    const sigBytes = new Uint8Array(
      await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(message)),
    );
    // CLOB decodes POLY_SIGNATURE with `base64.urlsafe_b64decode` — must be
    // base64url, not the standard `+`/`/` alphabet.
    return base64UrlEncode(sigBytes);
  }

  private async buildAuthHeaders(
    method: string,
    requestPath: string,
    body: string = "",
  ): Promise<Record<string, string>> {
    if (!this.auth) throw new Error("polymarket: no auth");
    const timestamp = String(Math.floor(Date.now() / 1000));
    const nonce = String(Date.now());
    const signature = await this.buildHmacSignature(
      method,
      requestPath,
      body,
      timestamp,
    );
    return {
      POLY_ADDRESS: this.auth.address,
      POLY_API_KEY: this.auth.credentials.apiKey,
      POLY_SIGNATURE: signature,
      POLY_TIMESTAMP: timestamp,
      POLY_NONCE: nonce,
      POLY_PASSPHRASE: this.auth.credentials.passphrase,
    };
  }

  private async clobPostAuth<T>(path: string, body: unknown): Promise<T> {
    const bodyStr = JSON.stringify(body);
    const headers = {
      "Content-Type": "application/json",
      ...(await this.buildAuthHeaders("POST", path, bodyStr)),
    };
    const res = await fetch(`${this.clobUrl}${path}`, {
      method: "POST",
      headers,
      body: bodyStr,
      signal: AbortSignal.timeout(15_000),
    });
    if (!res.ok) {
      const msg = await res.text().catch(() => "");
      throw new Error(`CLOB ${res.status} POST ${path}${msg ? `: ${msg}` : ""}`);
    }
    return (await res.json()) as T;
  }

  private async clobDeleteAuth<T>(path: string): Promise<T> {
    const headers = await this.buildAuthHeaders("DELETE", path);
    const res = await fetch(`${this.clobUrl}${path}`, {
      method: "DELETE",
      headers,
      signal: AbortSignal.timeout(10_000),
    });
    if (!res.ok) throw new Error(`CLOB ${res.status} DELETE ${path}`);
    return (await res.json()) as T;
  }
}

// ---------------------------------------------------------------------------
// Wire serialization
// ---------------------------------------------------------------------------

const ZERO_BUILDER = `0x${"0".repeat(64)}`;

/**
 * Serialize a signed CTF order for the CLOB wire format.
 *
 * The OpenAPI schema omits some fields (`taker`, `feeRateBps`) but the CLOB
 * service rejects orders without them — we ride them on the wire anyway.
 */
function serializeSignedOrder(
  signed: SignedClobOrder,
  side: "BUY" | "SELL",
): Record<string, unknown> {
  const o = signed.order;
  return {
    // `salt` is the only numeric field the v2 schema expects as an integer;
    // all other uint256 fields ride as strings. Fits in int53 because
    // buildCTFOrder uses a 10-digit random salt.
    salt: Number(o.salt),
    maker: o.maker,
    signer: o.signer,
    taker: o.taker,
    tokenId: o.tokenId.toString(),
    makerAmount: o.makerAmount.toString(),
    takerAmount: o.takerAmount.toString(),
    side,
    expiration: o.expiration.toString(),
    nonce: o.nonce.toString(),
    timestamp: o.nonce.toString(),
    feeRateBps: o.feeRateBps.toString(),
    metadata: "",
    builder: ZERO_BUILDER,
    signature: signed.signature,
    signatureType: o.signatureType,
  };
}

// ---------------------------------------------------------------------------
// Base64 helpers (browser-safe)
// ---------------------------------------------------------------------------

function base64Decode(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function base64Encode(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

/** URL-safe base64 (RFC 4648 §5) — matches Python's `base64.urlsafe_b64encode`. */
function base64UrlEncode(bytes: Uint8Array): string {
  return base64Encode(bytes).replace(/\+/g, "-").replace(/\//g, "_");
}
